/**
 * WidgetSelector - Checkbox grid for widget selection
 *
 * Fetches available widgets from widget_registry (DB-backed),
 * falls back to hardcoded ALL_WIDGETS if the API is unavailable.
 */

import React, { useState, useEffect } from 'react';
import { ALL_WIDGETS } from '../../constants/profile_constants.js';
import { widgetRegistryAPI } from '../../../api/admin.js';

export function WidgetSelector({ selectedWidgets, onChange }) {
  const [widgets, setWidgets] = useState(ALL_WIDGETS);

  useEffect(() => {
    widgetRegistryAPI.getActive()
      .then(res => {
        if (res?.ok && Array.isArray(res.data) && res.data.length > 0) {
          setWidgets(res.data.map(w => ({ id: w.code, label: w.label, description: w.description, category: w.category })));
        }
      })
      .catch(() => { /* keep hardcoded fallback */ });
  }, []);

  const handleToggle = (widgetId) => {
    const newWidgets = selectedWidgets.includes(widgetId)
      ? selectedWidgets.filter(w => w !== widgetId)
      : [...selectedWidgets, widgetId];
    onChange(newWidgets);
  };

  const handleSelectAll = () => {
    onChange(widgets.map(w => w.id));
  };

  const handleSelectNone = () => {
    onChange([]);
  };

  // Group widgets by category for better organisation
  const categories = [...new Set(widgets.map(w => w.category || 'general'))];
  const CATEGORY_LABELS = {
    operational: 'Opérationnel',
    fleet: 'Flotte',
    monitoring: 'Suivi',
    financial: 'Financier',
    general: 'Général',
  };

  return (
    <div className="space-y-3">
      {/* Header with quick actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-300">
          Widgets Dashboard ({selectedWidgets.length}/{widgets.length})
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
          >
            Tout
          </button>
          <button
            type="button"
            onClick={handleSelectNone}
            className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
          >
            Aucun
          </button>
        </div>
      </div>

      {/* Widgets grid grouped by category */}
      {categories.map(cat => {
        const catWidgets = widgets.filter(w => (w.category || 'general') === cat);
        if (catWidgets.length === 0) return null;
        return (
          <div key={cat}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              {CATEGORY_LABELS[cat] || cat}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {catWidgets.map(widget => (
                <label
                  key={widget.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedWidgets.includes(widget.id)
                      ? 'bg-blue-500/20 border border-blue-500/30'
                      : 'bg-slate-700/30 border border-transparent hover:bg-slate-700/50'
                  }`}
                  title={widget.description || ''}
                >
                  <input
                    type="checkbox"
                    checked={selectedWidgets.includes(widget.id)}
                    onChange={() => handleToggle(widget.id)}
                    className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <div>
                    <div className={`text-sm font-medium ${
                      selectedWidgets.includes(widget.id) ? 'text-white' : 'text-slate-300'
                    }`}>
                      {widget.label}
                    </div>
                    <div className="text-xs text-slate-500">{widget.id}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default WidgetSelector;
